import { timestampFromDate } from '@bufbuild/protobuf/wkt'
import { utc } from '@date-fns/utc'
import {
    Card,
    CardContent,
    Box,
    Typography,
    TextField,
    Autocomplete,
} from '@mui/material'
import { BarChart } from '@mui/x-charts'
import { createFileRoute, stripSearchParams } from '@tanstack/react-router'
import { addMonths, formatISO, startOfMonth } from 'date-fns'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'react-oidc-context'
import { z } from 'zod/v4-mini'
import { I18NAwareLocalizationProvider } from '@/components/I18NAwareLocalizationProvider'
import { StatisticsPendingComponent } from '@/features/statistics/components/StatisticsPendingComponent'
import { useHoursPerMonths } from '@/features/statistics/hooks/useHoursPerMonths'
import { useHoursPerProjects } from '@/features/statistics/hooks/useHoursPerProjects'
import { DeletedItemSelection } from '@/generated/Enumerations/DeletedItemSelection_pb'
import { ProjectSortBy } from '@/generated/Enumerations/ProjectSortBy_pb'
import { parseId, stringifyId } from '@/helper/id'
import { useAppForm } from '@/hooks/useAppForm'
import { projectsQueryOptions, useProjects } from '@/queries/useProjects'
import { trackingsQueryOptions, useTrackings } from '@/queries/useTrackings'
import {
    searchParamsProjectIds,
    searchParamsProjectIdsDefaultValue,
} from '@/schemas/searchParams/searchParamsProjectIds'
import { searchParamsToFromSchema } from '@/schemas/searchParams/searchParamsToFromSchema'

const seleniumPrefix = 'statistics'

const today = new Date()

const getStart = (date: string | null | undefined) =>
    date === undefined || date === null ? startOfMonth(today, { in: utc })
    : typeof date === 'string' ? new Date(date)
    : date

const getEnd = (date: string | null | undefined) =>
    date === undefined || date === null ?
        startOfMonth(addMonths(today, 1), { in: utc })
    : typeof date === 'string' ? new Date(date)
    : date

const RouteComponent: React.FC = () => {
    const { user } = useAuth()
    const { t } = useTranslation()

    const { from, to, projectIds } = Route.useSearch()
    const parsedDateFrom = React.useMemo(() => getStart(from), [from])
    const parsedDateTo = React.useMemo(() => getEnd(to), [to])

    const navigate = Route.useNavigate()

    const form = useAppForm({
        defaultValues: {
            dateFrom: parsedDateFrom,
            dateTo: parsedDateTo,
            projectIds,
        },
        onSubmit: ({ value: { dateFrom, dateTo, projectIds } }) =>
            navigate({
                search: prev => ({
                    ...prev,
                    from: formatISO(dateFrom, {
                        representation: 'date',
                    }),
                    to: formatISO(dateTo, {
                        representation: 'date',
                    }),
                    projectIds,
                }),
                replace: true,
            }),
        listeners: {
            onChange: ({ formApi }) => formApi.handleSubmit(),
            onChangeDebounceMs: 1000,
        },
    })

    const {
        data: { projects },
    } = useProjects({
        userIds: user?.profile.sub ? [parseId(user.profile.sub)] : undefined,
        deletedItemSelection: DeletedItemSelection.NORMAL_ONLY,
        sorting: [{ sortBy: ProjectSortBy.NAME }],
        dateFrom: timestampFromDate(parsedDateFrom),
        dateTo: timestampFromDate(parsedDateTo),
    })
    const {
        data: { trackings },
    } = useTrackings(
        {
            userIds:
                user?.profile.sub ? [parseId(user.profile.sub)] : undefined,
            projectIds: projectIds.map(parseId),
            hasEnded: true,
            dateTimeFrom: timestampFromDate(parsedDateFrom),
            dateTimeTo: timestampFromDate(parsedDateTo),
            deletedItemSelection: DeletedItemSelection.NORMAL_ONLY,
        },
        true
    )

    const hoursPerProjects = useHoursPerProjects(projects, trackings)

    const hoursPerMonths = useHoursPerMonths(
        parsedDateFrom,
        parsedDateTo,
        trackings
    )

    return (
        <Card>
            <title>{t('common.statistics')}</title>

            <CardContent>
                <form.AppForm>
                    <Box
                        component={'form'}
                        sx={{
                            my: 2,
                            display: 'flex',
                            gap: 2,
                            alignItems: 'center',
                            flexWrap: 'wrap',
                        }}
                    >
                        <I18NAwareLocalizationProvider>
                            <form.AppField name={'dateFrom'}>
                                {field => (
                                    <field.DatePicker
                                        label={t('common.from')}
                                        sx={{ flex: 1, minWidth: 200 }}
                                        data-selenium={`${seleniumPrefix}-from-date-time`}
                                    />
                                )}
                            </form.AppField>

                            <form.AppField name={'dateTo'}>
                                {field => (
                                    <field.DatePicker
                                        label={t('common.to')}
                                        sx={{ flex: 1, minWidth: 200 }}
                                        data-selenium={`${seleniumPrefix}-to-date-time`}
                                    />
                                )}
                            </form.AppField>
                        </I18NAwareLocalizationProvider>

                        <form.AppField name={'projectIds'}>
                            {field => (
                                <Autocomplete
                                    multiple={true}
                                    options={projects}
                                    getOptionKey={p => p.name}
                                    getOptionLabel={p => p.name}
                                    value={projects.filter(p =>
                                        field.state.value.includes(
                                            stringifyId(p.id)
                                        )
                                    )}
                                    isOptionEqualToValue={(a, b) =>
                                        stringifyId(a.id) === stringifyId(b.id)
                                    }
                                    onChange={(_, value) =>
                                        field.handleChange(
                                            value.map(p => stringifyId(p.id))
                                        )
                                    }
                                    renderInput={params => (
                                        <TextField
                                            label={t('common.projects')}
                                            {...params}
                                        />
                                    )}
                                    style={{ flex: 1, minWidth: 200 }}
                                    data-selenium={`${seleniumPrefix}-projects`}
                                    disableCloseOnSelect={true}
                                    onBlur={field.handleBlur}
                                />
                            )}
                        </form.AppField>
                    </Box>
                </form.AppForm>

                <Typography variant={'h6'} sx={{ mt: 3 }}>
                    {t('statistics.hoursPerProject')}
                </Typography>

                {hoursPerProjects.length > 0 ?
                    <BarChart
                        xAxis={[
                            {
                                data: hoursPerProjects.map(
                                    ({ project }) => project?.name ?? ''
                                ),
                                scaleType: 'band',
                                colorMap: {
                                    type: 'ordinal',
                                    colors: hoursPerProjects.map(
                                        ({ project }) =>
                                            project?.displayColor ?? ''
                                    ),
                                },
                            },
                        ]}
                        yAxis={[{ label: t('common.hours') }]}
                        series={[
                            {
                                data: hoursPerProjects.map(
                                    ({ hours }) => hours
                                ),
                                valueFormatter: value =>
                                    `${value?.toFixed(2)}h`,
                            },
                        ]}
                        sx={{ height: 300, mt: 1 }}
                    />
                :   <Typography>{t('statistics.noData')}</Typography>}

                <Typography variant={'h6'} sx={{ mt: 3 }}>
                    {t('statistics.hoursInTimespan')}
                </Typography>

                {hoursPerMonths.length > 0 ?
                    <BarChart
                        series={[
                            {
                                data: hoursPerMonths.map(({ hours }) => hours),
                                valueFormatter: value =>
                                    `${value?.toFixed(2)}h`,
                            },
                        ]}
                        xAxis={[
                            {
                                scaleType: 'band',
                                data: hoursPerMonths.map(
                                    ({ dateString: date }) => date
                                ),
                            },
                        ]}
                        yAxis={[{ label: t('common.hours') }]}
                        sx={{ height: 300, mt: 1 }}
                    />
                :   <Typography>{t('statistics.noData')}</Typography>}
            </CardContent>
        </Card>
    )
}

export const Route = createFileRoute('/__base-layout/statistics')({
    component: RouteComponent,
    pendingMs: 2000,
    pendingComponent: StatisticsPendingComponent,

    validateSearch: z.object({
        ...searchParamsToFromSchema.shape,
        ...searchParamsProjectIds.shape,
    }),

    search: {
        middlewares: [stripSearchParams(searchParamsProjectIdsDefaultValue)],
    },

    beforeLoad: ({
        context: { queryClient, grpcClients, user },
        search: { from, to, projectIds },
    }) =>
        Promise.all([
            queryClient.ensureQueryData(
                trackingsQueryOptions(
                    grpcClients,
                    {
                        userIds:
                            user?.profile.sub ?
                                [parseId(user.profile.sub)]
                            :   undefined,
                        projectIds: projectIds.map(parseId),
                        hasEnded: true,
                        dateTimeFrom: timestampFromDate(getStart(from)),
                        dateTimeTo: timestampFromDate(getEnd(to)),
                        deletedItemSelection: DeletedItemSelection.NORMAL_ONLY,
                    },
                    true
                )
            ),
            queryClient.ensureQueryData(
                projectsQueryOptions(grpcClients, {
                    userIds:
                        user?.profile.sub ?
                            [parseId(user.profile.sub)]
                        :   undefined,
                    sorting: [{ sortBy: ProjectSortBy.NAME }],
                })
            ),
        ]),
})
